import {FormApi} from 'final-form';
import React, {MutableRefObject, PropsWithChildren} from 'react';
import {Form, FormProps, FormSpy} from 'react-final-form';
import {isEqual} from 'lodash';

export interface FinalFormProps<T> {
    submitText?: string;
    formSettings: FormProps<T>;
    disabled?: boolean;
    className?: string;
    cancelText?: string;
    onCancel?: () => void;
    onChange?: (values: T) => void;
    withDebug?: boolean;
    withMaxWidth?: boolean;
}

interface FinalFormOwnProps<T = {}> {
    forwardedRef?:
        | ((instance: FormApi<T> | null) => void)
        | MutableRefObject<FormApi<T> | null>
        | null;
}

class FinalForm<T extends {}> extends React.Component<FinalFormProps<T> & FinalFormOwnProps<T>> {
    static defaultProps = {
        submitting: false,
    };

    shouldComponentUpdate(nextProps: FinalFormProps<T>) {
        const {submitText, formSettings, disabled, className, cancelText, onCancel} = this.props;
        if (
            submitText !== nextProps.submitText ||
            disabled !== nextProps.disabled ||
            className !== nextProps.className ||
            cancelText !== nextProps.cancelText ||
            onCancel !== nextProps.onCancel
        ) {
            return true;
        }

        return !isEqual(formSettings, nextProps.formSettings);
    }

    render() {
        const {children, className, formSettings, withDebug, onChange, forwardedRef} = this.props;
        const formProps = {...formSettings, onSubmit: this.getHandleSubmit(this.props)};

        return (
            <Form {...formProps}>
                {({handleSubmit, form}) => {
                    // react-final-form doesn't provide ref since 6.0.0
                    if (typeof forwardedRef === 'function') {
                        forwardedRef(form);
                    } else if (forwardedRef) {
                        (forwardedRef as MutableRefObject<FormApi<T>>).current = form;
                    }

                    return (
                        <form
                            className={className}
                            onSubmit={handleSubmit}
                            onKeyDown={(event) => {
                                if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                                    handleSubmit();
                                }
                            }}
                        >
                            {children}
                            <FormSpy<T>
                                subscription={{values: true}}
                                onChange={(formState) => {
                                    if (onChange) {
                                        onChange(formState.values);
                                    }

                                    if (withDebug) {
                                        console.log(
                                            '%cFORM STATE UPDATED',
                                            'background: #222; color: #bada55',
                                            formState.values,
                                        );
                                    }
                                }}
                            />
                        </form>
                    );
                }}
            </Form>
        );
    }

    private getHandleSubmit(props: FinalFormProps<T>) {
        return props.formSettings.onSubmit;
    }
}

export default React.forwardRef<FormApi, PropsWithChildren<FinalFormProps<Object>>>(
    (props, ref) => <FinalForm forwardedRef={ref} {...props} />,
);
